import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { DistanceValueUnit } from '@common/enums/configuration/units';
import useDistanceUnitValue from '@common/hooks/useDistanceUnitValue';
import { FleetDashboardFilters } from 'models/fleet-management/FleetDataFilter';
import { DateTime } from 'luxon';
import { FleetDataPerformanceChange } from 'models/fleet-management/FleetDataSet';
import CustomTable from 'components/misc/CustomTable';
import { useFleetInsights } from 'hooks/useFleetDataset';
import {
  FLEET_SECTION_GAP,
  HEALTH_STATUS_LABEL_KEYS,
  HEALTH_TONES,
  PERFORMANCE_TONE,
} from 'utils/fleet-dashboard-theme';
import {
  PerformanceFilter,
  filterPerformanceRows,
  formatRatioDelta,
  ratioDeviation,
} from 'utils/performance-changes';
import DashboardSection from '../DashboardSection';
import DatasetState from './DatasetState';
import { TableSkeleton } from './SectionSkeletons';
import useTrendQuery from 'hooks/useTrendQuery';
import RobotPerformanceDetail from './RobotPerformanceDetail';
import useShowDealerColumn from 'hooks/useShowDealerColumn';

/**
 * Last week against the baseline as a bar either side of 1.00: a decline fills left in the
 * performance hue, an improvement is outlined right, and neither borrows a health colour.
 */
const RatioCell: React.FC<{ ratio: number | null }> = ({ ratio }) => {
  if (ratio == null) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  const deviation = ratioDeviation(ratio);
  const half = `${Math.abs(deviation) * 50}%`;
  const declining = deviation < 0;

  return (
    <Stack direction="row" alignItems="center" gap={1} width="100%" height="100%">
      <Box position="relative" flex={1} height={20} minWidth={60}>
        <Box
          position="absolute"
          top={0}
          bottom={0}
          left="50%"
          width="1px"
          sx={{ backgroundColor: 'divider' }}
        />
        <Box
          position="absolute"
          top={4}
          bottom={4}
          sx={
            declining
              ? {
                  right: '50%',
                  width: half,
                  borderRadius: '3px 0 0 3px',
                  backgroundColor: PERFORMANCE_TONE.mark,
                }
              : {
                  left: '50%',
                  width: half,
                  borderRadius: '0 3px 3px 0',
                  backgroundColor: PERFORMANCE_TONE.tint,
                  border: `1.5px solid ${PERFORMANCE_TONE.mark}`,
                  boxSizing: 'border-box',
                }
          }
        />
      </Box>
      <Typography
        variant="body2"
        width={36}
        textAlign="right"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {ratio.toFixed(2)}
      </Typography>
      <Typography
        variant="caption"
        width={52}
        color={PERFORMANCE_TONE.ink}
        fontWeight={declining ? 700 : 400}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {`${declining ? '▼' : '▲'} ${formatRatioDelta(ratio)}`}
      </Typography>
    </Stack>
  );
};

const FleetPerformanceWidgets: React.FC<{
  filters: FleetDashboardFilters;
}> = ({ filters }) => {
  const { t, i18n } = useTranslation();
  const { multiplier, label: areaUnit } = useDistanceUnitValue(1, DistanceValueUnit.METERS_SQUARED);

  const [filter, setFilter] = useState<PerformanceFilter>(PerformanceFilter.ALL);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // The trend and the robot detail look back a fixed run of weeks, whatever window the bar holds.
  const { query, trendQuery, grid } = useTrendQuery(filters);

  const performance = useFleetInsights<FleetDataPerformanceChange[]>('performance-changes', query);

  const rows = useMemo(
    () => filterPerformanceRows(performance.data ?? [], filter),
    [filter, performance.data],
  );
  const selected = (performance.data ?? []).find((row) => row.id === selectedId) ?? null;

  const area = (value: number) =>
    `${Math.round(value * multiplier).toLocaleString(i18n.language)} ${areaUnit}`;

  const showDealerColumn = useShowDealerColumn(filters);
  const allColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('fleet-management-field-robot'),
      width: 160,
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row" alignItems="center" gap={1} height="100%" minWidth={0}>
          <Box
            sx={{
              width: 3,
              height: 18,
              borderRadius: 1,
              flexShrink: 0,
              backgroundColor: params.row.id === selectedId ? PERFORMANCE_TONE.mark : 'transparent',
            }}
          />
          <Stack minWidth={0}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {params.row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {params.row.serial_number}
            </Typography>
          </Stack>
        </Stack>
      ),
    },
    {
      field: 'dealer',
      headerName: t('fleet-management-field-dealer'),
      width: 160,
      valueGetter: (value) => value ?? '—',
    },
    {
      field: 'customer',
      headerName: t('fleet-management-field-customer'),
      width: 170,
      valueGetter: (value) => value ?? '—',
    },
    {
      field: 'model',
      headerName: t('fleet-management-field-model'),
      width: 90,
      valueGetter: (value) => value ?? '—',
    },
    {
      field: 'hardware_version__robot_generation',
      headerName: t('fleet-management-field-hardware_version__robot_generation'),
      width: 110,
      valueGetter: (value) => value ?? '—',
    },
    {
      field: 'software_version__rsw_software_version',
      headerName: t('fleet-management-column-rsw'),
      width: 100,
      valueGetter: (value) => value ?? '—',
    },
    {
      field: 'customer_journey_status',
      headerName: t('fleet-management-field-robot-health_status'),
      width: 130,
      renderCell: (params: GridRenderCellParams) => {
        const status = params.row
          .customer_journey_status as FleetDataPerformanceChange['customer_journey_status'];
        const tone = HEALTH_TONES[status];
        return tone ? (
          <Chip
            size="small"
            label={t(HEALTH_STATUS_LABEL_KEYS[status])}
            sx={{ fontWeight: 600, color: tone.ink, backgroundColor: tone.tint }}
          />
        ) : (
          '—'
        );
      },
    },
    {
      field: 'last_week_area',
      headerName: t('fleet-management-column-last-week-area'),
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number) => area(value ?? 0),
    },
    {
      field: 'baseline_weekly_area',
      headerName: t('fleet-management-column-baseline-area'),
      description: t('fleet-management-info-baseline-area'),
      width: 140,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number | null) =>
        value == null ? t('fleet-management-no-baseline') : area(value),
    },
    {
      field: 'ratio',
      headerName: t('fleet-management-column-change-against-baseline'),
      width: 230,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => <RatioCell ratio={params.row.ratio} />,
    },
    {
      field: 'operating_hours',
      headerName: t('fleet-management-column-hours'),
      width: 90,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number) => (value ?? 0).toFixed(1),
    },
    {
      field: 'last_successful_mission_date',
      headerName: t('fleet-management-field-last_successful_mission_date'),
      width: 150,
      valueFormatter: (value: string | null) =>
        value
          ? DateTime.fromISO(value).setLocale(i18n.language).toLocaleString(DateTime.DATE_MED)
          : '—',
    },
  ];
  const columns = showDealerColumn
    ? allColumns
    : allColumns.filter((column) => column.field !== 'dealer');

  const filterPills = (
    <Stack
      direction="row"
      gap={0.75}
      flexWrap="wrap"
      role="group"
      aria-label={t('fleet-management-filter-performance')}
    >
      {[
        { key: PerformanceFilter.ALL, labelKey: 'fleet-management-performance-filter-all' },
        {
          key: PerformanceFilter.DECLINING,
          labelKey: 'fleet-management-performance-filter-declining',
        },
        {
          key: PerformanceFilter.NO_BASELINE,
          labelKey: 'fleet-management-performance-filter-no-baseline',
        },
      ].map(({ key, labelKey }) => {
        const isSelected = filter === key;
        return (
          <Chip
            key={key}
            size="small"
            variant="outlined"
            label={t(labelKey)}
            aria-pressed={isSelected}
            onClick={() => setFilter(key)}
            sx={{
              fontWeight: isSelected ? 700 : 500,
              borderWidth: isSelected ? 2 : 1,
              borderColor: isSelected ? PERFORMANCE_TONE.mark : undefined,
              backgroundColor: isSelected ? PERFORMANCE_TONE.tint : 'transparent',
              color: isSelected ? PERFORMANCE_TONE.ink : undefined,
            }}
          />
        );
      })}
    </Stack>
  );

  return (
    <Stack gap={FLEET_SECTION_GAP} width="100%">
      <DashboardSection
        title={t('fleet-management-title-performance-ranking')}
        caption={t('fleet-management-caption-performance-ranking')}
        info={t('fleet-management-info-performance-ranking')}
        actions={filterPills}
      >
        <DatasetState
          isLoading={performance.isLoading}
          hasError={performance.hasError}
          hasData={performance.data != null}
          skeleton={<TableSkeleton />}
        >
          <Box
            sx={{
              '& .performance-selected .MuiDataGrid-cell': {
                backgroundColor: '#F3F3FE',
              },
            }}
          >
            <CustomTable
              getStatus={HttpFetchStatus.Success}
              dataLoaded
              failedToLoadMessage={t('fleet-management-error')}
              emptyDataMessage={t('fleet-management-no-performance-rows')}
              data={rows}
              columns={columns}
              rowClickHandler={(id) => setSelectedId((current) => (current === id ? null : id))}
              getRowClassName={(params) => (params.id === selectedId ? 'performance-selected' : '')}
              autoHeight
            />
          </Box>
        </DatasetState>
      </DashboardSection>

      {/* The detail opens over the table, so the ranking stays where it was when it closes. */}
      {selected && (
        <RobotPerformanceDetail
          robot={selected}
          query={trendQuery}
          grid={grid}
          onClose={() => setSelectedId(null)}
        />
      )}
    </Stack>
  );
};

export default React.memo(FleetPerformanceWidgets);
